import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import api from "../utils/axios";
import axios from "axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";
import { useContext } from "react";
import { AuthContext } from "context/AuthContext";
import { useNavigate } from "react-router-dom";

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) => api.put(API_ROUTES.UPDATE_USER, data),
      onSuccess: () => {
        showToast("success", "User updated successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_USER);
      },
    });
  return { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending };
};

export const useGetUserByIdQuery = (id) => {
  const queryClient = new QueryClient();
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_USER, id],
    queryFn: () => api.get(`${API_ROUTES.GET_USER}/${id}`),
    enabled: !!id,
  });

  return { isPending, isError, user: data?.data?.data, error };
};

export const useGetUserQuery = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Append any key-value pairs dynamically to query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : value
      );
    }
  });

  const queryString = queryParams.toString();
  const url = queryString
    ? `${API_ROUTES.GET_USER}?${queryString}`
    : API_ROUTES.GET_USER;

  const { data, isError, error, isLoading } = useQuery({
    queryKey: [API_ROUTES.GET_USER, params],
    queryFn: () => api.get(url),
  });

  return { data: data?.data?.data ?? [], isLoading, isError, error };
};

export const useLogoutMutation = () => {
  const { logout } = useContext(AuthContext);
  const router = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      // logout() issues the request itself — calling api.get(LOGOUT) here too
      // sent /logout twice per sign-out.
      mutationFn: () => logout(),
      onSuccess: () => {
        router("/login");
        // window.location.href = '/login'
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_USER);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useAddAdminMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) => api.post(API_ROUTES.ADD_ADMIN, data),
      onSuccess: () => {
        showToast("success", "Admin added successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_USER);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useGetAdminQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_ADMIN],
    queryFn: () => api.get(API_ROUTES.GET_ADMIN),
  });
  return { data: data?.data?.admins ?? [], isPending, isError, error };
};

export const useDeleteAdminMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) => api.delete(`${API_ROUTES.DELETE_ADMIN}/${id}`),
      onSuccess: () => {
        showToast("success", "Admin deleted successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_USER);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateAdminMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(`${API_ROUTES.UPDATE_ADMIN}/${data?.id}`, data),
      onSuccess: () => {
        showToast("success", "Admin updated successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_USER);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useResyncUserMutation = () => {
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: () =>
        axios.post("https://www.ausrealty.com.au/api/agent/sync"),
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useGetUserActivityQuery = () => {
  const { data, isError, error, isLoading } = useQuery({
    queryKey: [API_ROUTES.GET_USER_ACTIVITY],
    queryFn: () => api.get(API_ROUTES.GET_USER_ACTIVITY),
  });
  return { data: data?.data?.data ?? [], isLoading, isError, error };
};

// Wallet balances + recent ledger rows for one user (superAdmin only).
export const useGetUserCreditsQuery = (id) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_USER, id, "credits"],
    queryFn: () => api.get(`${API_ROUTES.GET_USER}/${id}/credits`),
    enabled: !!id,
  });
  return { data: data?.data?.data, isLoading, isError, error };
};

// Grant (amount > 0) or remove (amount < 0) credits on a user's wallet.
export const useAdjustUserCreditsMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, ...body }) =>
      api.post(`${API_ROUTES.GET_USER}/${id}/credits`, body),
    onSuccess: () => {
      showToast("success", "Credits updated");
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to update credits"
      );
    },
    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.GET_USER, id, "credits"],
      });
    },
  });
  return { mutate, isPending };
};
